import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { cart as cartApi } from '../utils/api';
import { useAuth } from './AuthContext';

const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const { user } = useAuth();
  const [cartItems, setCartItems] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    return localStorage.getItem('cart_session_id') || null;
  });

  const loadCart = useCallback(async () => {
    try {
      const data = await cartApi.get(sessionId);
      setCartItems(data.items || []);
    } catch (error) {
      console.error('Failed to load cart', error);
    }
  }, [sessionId]);

  useEffect(() => {
    loadCart();
  }, [user, sessionId, loadCart]);

  const addToCart = async (productId, size, quantity = 1) => {
    try {
      const response = await cartApi.add({ product_id: productId, size, quantity }, sessionId);
      if (response.session_id && !user) {
        setSessionId(response.session_id);
        localStorage.setItem('cart_session_id', response.session_id);
      }
      await loadCart();
      setDrawerOpen(true);
    } catch (error) {
      console.error('Failed to add to cart', error);
      throw error;
    }
  };

  const updateCartItem = async (productId, size, quantity) => {
    try {
      await cartApi.update({ product_id: productId, size, quantity }, sessionId);
      await loadCart();
    } catch (error) {
      console.error('Failed to update cart', error);
    }
  };

  const removeFromCart = async (productId, size) => {
    try {
      await cartApi.remove(productId, size, sessionId);
      await loadCart();
    } catch (error) {
      console.error('Failed to remove from cart', error);
    }
  };

  const clearCart = () => {
    setCartItems([]);
    if (!user) {
      localStorage.removeItem('cart_session_id');
      setSessionId(null);
    }
  };

  const openDrawer = () => setDrawerOpen(true);
  const closeDrawer = () => setDrawerOpen(false);

  const cartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);
  const cartTotal = cartItems.reduce((sum, item) => sum + (item.product?.price || 0) * item.quantity, 0);

  const FREE_SHIPPING_THRESHOLD = 50;
  const shippingProgress = Math.min((cartTotal / FREE_SHIPPING_THRESHOLD) * 100, 100);
  const shippingRemaining = Math.max(FREE_SHIPPING_THRESHOLD - cartTotal, 0);

  return (
    <CartContext.Provider value={{
      cartItems, addToCart, updateCartItem, removeFromCart, clearCart,
      cartCount, cartTotal, loadCart,
      drawerOpen, openDrawer, closeDrawer,
      FREE_SHIPPING_THRESHOLD, shippingProgress, shippingRemaining
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
